/* Files on a filed ticket (S09): pick, send, list.
 *
 * The SERVER is the authority (api/src/functions/ticketFiles.js, api/src/shared/fileRules.js). The
 * checks here only stop an obvious "no" before a long upload, with the same limits and words.
 *
 * ORDER. A file is sent AFTER its ticket or reply is saved, one request per file, naming the
 * message it belongs to (the id the server's reply RECEIPT gives - the stored id, not the one the
 * browser made up). A file that fails does not undo the ticket or the reply: the screen says which
 * file did not arrive, so the person can add it again in a new reply.
 *
 * RETRY. Each picked file carries one key for its whole life. A send whose answer is lost is sent
 * again with the SAME key, and the server finishes that same file instead of storing a second one.
 */

import { guardedFetch } from './liveWrites.js';

export const MAX_BYTES = 10 * 1024 * 1024;
export const MAX_FILES = 5;
const TYPES = {
  'image/png': 'png', 'image/jpeg': 'jpg', 'image/gif': 'gif', 'image/webp': 'webp', 'application/pdf': 'pdf',
};
const EXTS = new Set(['png', 'jpg', 'jpeg', 'gif', 'webp', 'pdf']);
/* What the file chooser offers. */
export const ACCEPT = `${Object.keys(TYPES).join(',')},.png,.jpg,.jpeg,.gif,.webp,.pdf`;
const RETRIES = 2;

const extOf = (name) => String(name || '').split('.').pop().toLowerCase();

/* Does this look like a file the server takes? A browser may leave `type` empty, so the ending is
 * accepted too. The server reads the bytes anyway: a renamed file is refused there. */
function looksAllowed(file) {
  return Boolean(TYPES[file.type]) || (!file.type && EXTS.has(extOf(file.name)));
}

export function sizeLabel(bytes) {
  const n = Number(bytes) || 0;
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

export const isPdf = (f) => f && (f.type === 'application/pdf' || extOf(f.name) === 'pdf');

/* Add newly chosen files to the ones already picked. Answers { picks, refused } where refused is a
 * list of plain sentences, one per file that was not added. */
export function addPicks(current, chosen, { newKey = () => crypto.randomUUID() } = {}) {
  const picks = [...current];
  const refused = [];
  for (const file of Array.from(chosen || [])) {
    if (picks.length >= MAX_FILES) {
      refused.push(`${file.name}: there are already ${MAX_FILES} files. That is the most.`);
    } else if (!looksAllowed(file)) {
      refused.push(`${file.name}: only pictures (PNG, JPG, GIF, WebP) and PDF files can be added.`);
    } else if (file.size > MAX_BYTES) {
      refused.push(`${file.name}: too big (${sizeLabel(file.size)}). The most is 10 MB.`);
    } else if (file.size === 0) {
      refused.push(`${file.name}: the file is empty.`);
    } else {
      picks.push({ key: newKey(), file, name: file.name, size: file.size });
    }
  }
  return { picks, refused };
}

const filesPath = (ticketNo) => `/api/tickets/${encodeURIComponent(ticketNo)}/files`;
export const fileHref = (ticketNo, id) => `${filesPath(ticketNo)}/${encodeURIComponent(id)}`;

/* Send one picked file. Answers { ok: true, file } or { ok: false, why }. Never throws.
 * A lost answer, or a 5xx that may have stored it, is tried again with the same key. */
export async function sendFile(ticketNo, pick, { messageId = null, fetchFn = guardedFetch } = {}) {
  const url = `${filesPath(ticketNo)}${messageId ? `?messageId=${encodeURIComponent(messageId)}` : ''}`;
  let lastWhy = 'It could not be sent. Add it again in a new reply.';
  for (let attempt = 0; attempt <= RETRIES; attempt += 1) {
    let res;
    try {
      res = await fetchFn(url, {
        method: 'POST',
        headers: {
          'Content-Type': pick.file.type || 'application/octet-stream',
          'x-requested-by': 'ihs-it-help',
          'x-file-key': pick.key,
          // A header must be plain text: the server decodes it (fileRules.cleanName).
          'x-file-name': encodeURIComponent(pick.name),
        },
        body: pick.file,
      });
    } catch (err) {
      // The walkthrough build refuses before sending: nothing to retry.
      if (err && err.code === 'WALKTHROUGH_WRITE_BLOCKED') return { ok: false, why: err.message };
      continue;
    }
    let body = null;
    try { body = await res.json(); } catch { body = null; }
    if (res.ok && body && body.file) return { ok: true, file: body.file };
    const e = body && body.error;
    // A clear "no" from the server, in its own words: trying again would get the same answer.
    if (res.status >= 400 && res.status < 500) {
      return { ok: false, why: (e && typeof e.message === 'string' && e.message) || 'The server refused it.' };
    }
    if (res.status === 503 && e && e.code === 'NOT_READY') return { ok: false, why: e.message };
    lastWhy = (e && typeof e.message === 'string' && e.message) || lastWhy;
  }
  return { ok: false, why: lastWhy };
}

/* Send each picked file in turn. Answers { saved: [file...], failed: [{ name, why }] }. */
export async function sendFiles(ticketNo, picks, opts = {}) {
  const saved = [];
  const failed = [];
  for (const pick of picks) {
    const out = await sendFile(ticketNo, pick, opts);
    if (out.ok) saved.push(out.file);
    else failed.push({ name: pick.name, why: out.why });
  }
  return { saved, failed };
}

/* One sentence for a part-failed send, or '' when everything arrived. */
export function wordsForFailed(failed, { where }) {
  if (!failed.length) return '';
  const list = failed.map((f) => `${f.name} (${f.why})`).join('; ');
  return `${where} is saved, but ${failed.length === 1 ? 'this file was' : 'these files were'} not added: ${list} Add ${failed.length === 1 ? 'it' : 'them'} again in a new reply.`;
}

/* The files this reader may see on a filed ticket, or null when they could not be read. */
export async function listFiles(ticketNo, { fetchFn = guardedFetch } = {}) {
  try {
    const res = await fetchFn(filesPath(ticketNo));
    if (!res.ok) return null;
    const body = await res.json();
    return Array.isArray(body && body.files) ? body.files : null;
  } catch {
    return null;
  }
}
